<template>
    <DForm>
        <DFormRow
            :label="__('settings.roles.form.name')"
            :hint="__('settings.roles.form.name_hint')"
        >
            <DInput
                id="name"
                v-model="form.name"
                :invalid-feedback="errors.name"
                :is-valid="isFormFieldValid('name')"
                maxlength="255"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.roles.form.tenants')"
            :hint="__('settings.roles.form.tenants_hint')"
        >
            <DMultiSelect
                id="tenants"
                :options="tenantOptions"
                :invalid-feedback="errors.tenants"
                @update="updateSelectedTenants"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.roles.form.permissions')"
            :is-vertical="true"
            :hint="__('settings.roles.form.permissions_hint')"
        >
            <div
                v-for="perm in permissionsData"
                :key="perm"
                class="roleTag"
            >
                {{ perm }}
            </div>
            <DInput
                id="permission_search"
                v-model="permission_search"
                :invalid-feedback="errors.permissions"
                :is-valid="isFormFieldValid('permissions')"
                :placeholder="__('users.role.permissions._table.search_placeholder')"
                class="mt-3"
                maxlength="255"
            />
            <DTable
                :fields="fields"
                :items="filteredNonGraphqlPermissions"
            >
                <template #checkbox="{item}">
                    <td>
                        <DFormCheck
                            :id="`permission_${item.permission}`"
                            :checked="item.checked"
                            @change="togglePermission(item.permission)"
                        />
                    </td>
                </template>
                <template #permission="{item}">
                    <td>
                        {{ item.permission }}
                    </td>
                </template>
                <template #description="{item}">
                    <td>
                        {{ item.description }}
                    </td>
                </template>
            </DTable>
            <DButton
                :label="__('users.role.permissions.graphql_permissions')"
                @click="toggleGraphqlPermissions"
            />
            <DTable
                v-if="showGraphqlPermissions"
                class="mt-3"
                :fields="fields"
                :items="filteredGraphqlPermissions"
            >
                <template #checkbox="{item}">
                    <td>
                        <DFormCheck
                            :id="`permission_${item.permission}`"
                            :checked="item.checked"
                            @change="togglePermission(item.permission)"
                        />
                    </td>
                </template>
                <template #permission="{item}">
                    <td>
                        {{ item.permission }}
                    </td>
                </template>
                <template #description="{item}">
                    <td>
                        {{ item.description }}
                    </td>
                </template>
            </DTable>
        </DFormRow>
        <DFormControls
            :dirty="form.isDirty"
            :cancel-route="$route('settings.roles.index')"
            @submit="saveRole"
        />
    </DForm>
</template>

<script>

export default {
    name: 'RoleForm',
    props: {
        availablePermissions: {
            type: Array,
            default: () => { return [] }
        },
        errors: {
            type: Object,
            default: () => { return {} }
        },
        role: {
            type: Object,
            default () { return {} }
        },
        availableTenants: {
            type: Array,
            default: () => { return [] }
        },
        selectedTenants: {
            type: Array,
            default: () => { return [] }
        },
        selectedPermissions: {
            type: Array,
            default: () => { return [] }
        }
    },
    emits: ['saveRole'],
    data () {
        return {
            fields: [
                { key: 'checkbox', label: '', sorter: false },
                { key: 'permission', label: this.__('users.role.permissions._table.permission'), sorter: true },
                { key: 'description', label: this.__('users.role.permissions._table.description'), sorter: false }
            ],
            form: this.$inertia.form({
                name: this.role?.name,
                permissions: this.role.permissions
                    ? this.role.permissions.map(
                        permission => permission.name
                    )
                    : this.selectedPermissions,
                tenants: this.role.tenants ? this.role.tenants.map(tenant => tenant.id) : this.selectedTenants
            }),
            permission_search: '',
            showGraphqlPermissions: false
        }
    },
    computed: {
        filteredNonGraphqlPermissions () {
            return this.availablePermissions
                .map((permission) => ({
                    permission: permission.value,
                    checked: this.form.permissions.includes(permission.value),
                    description: permission.description
                }))
                .filter(({ permission }) => permission.includes(this.permission_search))
                .filter(({ permission }) => !permission.includes('graphql'))
        },
        filteredGraphqlPermissions () {
            return this.availablePermissions
                .map((permission) => ({
                    permission: permission.value,
                    checked: this.form.permissions.includes(permission.value),
                    description: permission.description
                }))
                .filter(({ permission }) => permission.includes(this.permission_search))
                .filter(({ permission }) => permission.includes('graphql'))
        },
        tenantOptions () {
            return this.availableTenants.map((item) => {
                return {
                    value: item,
                    text: item,
                    selected: this.form.tenants.includes(item)
                }
            })
        },
        permissionsData () {
            return this.sortPermissions()
        }
    },
    methods: {
        togglePermission (perm) {
            if (this.form.permissions.includes(perm)) {
                this.form.permissions.splice(this.form.permissions.indexOf(perm), 1)

                return
            }
            this.form.permissions.push(perm)
        },
        saveRole () {
            this.$emit('saveRole', this.form)
        },
        updateSelectedTenants (values) {
            this.form.tenants = values
        },
        toggleGraphqlPermissions () {
            this.showGraphqlPermissions = !this.showGraphqlPermissions
        },
        sortPermissions () {
            return this.form.permissions.sort()
        }
    }
}
</script>

<style lang="scss" scoped>
.roleTag {
    display: inline-block;
    justify-self: flex-start;
    background: var(--colorSecondaryLight);
    color: var(--colorPrimaryText);
    border-radius: 5px;
    font-size: 12px;
    margin: 2px 5px;
    padding: 5px 10px;
}
</style>
